import { Card, CardContent } from '@ui/card.tsx';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@r/components/ui/form.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { toast } from 'sonner';
import { HelpCircle } from 'lucide-react';
import { usePostHelp } from '@r/api/generated/client.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';

const HELP_REASONS = ['program', 'points', 'gift-order', 'other'] as const;

export const Contact = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();

  const FormSchema = z.object({
    reason: z.enum(HELP_REASONS, { required_error: t('form.fields.required') }),
    message: z.string({ required_error: t('form.fields.required') }).min(10)
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      message: ''
    }
  });

  const contactMutation = usePostHelp({
    mutation: {
      onSuccess: () => {
        form.reset();
        toast.success(t('need.help.contact.success'));
      },
      onError: (error) => {
        showOrvalErrorToast(error, t('need.help.contact.error'));
      }
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    contactMutation.mutate({
      data: { message: data.message, reason: t(`help.form.reason.${data.reason}`) }
    });
  }

  return (
    <div className="overflow-x-hidden flex-grow">
      <div className="pl-8 flex flex-col h-full text-foreground">
        <div className="mb-10 title">
          <div className="flex flex-nowrap items-center mb-3">
            <HelpCircle className="mr-3" />

            <h1 className="flex items-center font-bold text-2xl md:text-3xl lg:text-[2.75rem] lg:leading-[3.5rem]">
              {t('need.help.title')}
            </h1>
          </div>

          <p className="font-light text-muted-foreground">{t('need.help.description')}</p>
        </div>

        <div className="space-y-7">
          <Card className="flex flex-col justify-center w-full text-center border-0">
            <CardContent className="px-8 text-start">
              <div className="flex justify-center items-center mt-6 mb-6">
                <h3 className="text-2xl font-bold tracking-tight leading-none">
                  {t('need.help.contact.title')}
                </h3>
              </div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 w-full">
                  <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('need.help.contact.reason.label')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {HELP_REASONS.map((reason) => (
                              <SelectItem key={reason} value={reason}>
                                {t(`help.form.reason.${reason}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('need.help.contact.message.label')}</FormLabel>
                        <FormControl>
                          <Textarea rows={5} className="resize-none" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button loading={contactMutation.isPending} type="submit" rounded="full">
                    {t('need.help.contact.submit')}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
